import { BOARD_CELLS, cellIndex, inBounds } from './board';
import {
  BLACK_PROBABILITY,
  FIRST_REGULAR_TYPES,
  SECOND_REGULAR_TYPES,
  SHARED_TYPE,
  TARGET_COLOR,
} from './stones';
import type {
  GameState,
  Player,
  PlayerState,
  Rng,
  Stone,
  StoneColor,
  StoneType,
  WinCheckResult,
} from './types';
import { checkWin, findWinLine } from './win';

/** 各プレイヤーの観測可能回数（最大） */
export const MAX_OBSERVATIONS = 5;

/** ゲームロジック側の不正操作を示すエラー */
export class GameError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GameError';
  }
}

export function otherPlayer(player: Player): Player {
  return player === 'first' ? 'second' : 'first';
}

export function createPlayerState(): PlayerState {
  return {
    lastStoneType: null,
    lastRegularType: null,
    hasUsedRegular: false,
    observationsUsed: 0,
  };
}

/**
 * 指定プレイヤーが現在選択できる石の種類（交互使用規則・50% 連続禁止を含む）。
 * 純粋関数（Game インスタンスに依存しない）。Game と CPU（cpu.ts）で共有し、
 * 使用可能種類の判定ロジックを重複実装しないようにする。
 */
export function selectableStoneTypes(state: GameState, player: Player): StoneType[] {
  const ps = state.players[player];
  const regulars = player === 'first' ? FIRST_REGULAR_TYPES : SECOND_REGULAR_TYPES;
  const result: StoneType[] = [];
  for (const t of regulars) {
    // 通常の石は直前に使ったものとは異なる種類でなければいけない
    if (ps.hasUsedRegular && t === ps.lastRegularType) continue;
    result.push(t);
  }
  // 50% の石は、直前の自分の手番で 50% を使っていたら使えない
  if (ps.lastStoneType !== SHARED_TYPE) {
    result.push(SHARED_TYPE);
  }
  return result;
}

export function createInitialState(): GameState {
  return {
    board: new Array<Stone | null>(BOARD_CELLS).fill(null),
    currentPlayer: 'first',
    players: {
      first: createPlayerState(),
      second: createPlayerState(),
    },
    phase: 'selecting',
    winner: null,
    pendingDraw: false,
    moveCount: 0,
  };
}

/**
 * 3D量子五目並べのゲームエンジン。
 * Three.js 等の描画ライブラリに依存しない純粋なロジック実装。
 *
 * フェーズ遷移:
 *   selecting --placeStone--> deciding（観測権が残る場合）
 *   selecting --placeStone--> over（観測権なし＋盤面満杯 → 引き分け）
 *   selecting --placeStone--> selecting（観測権なし＋まだ空きあり → 相手手番）
 *   deciding  --observe-----> over（勝者決定）/ result（勝者なし）
 *   deciding  --skipObservation--> over（盤面満杯 → 引き分け）/ selecting（相手手番）
 *   result    --confirmResult--> over（引き分け）/ selecting（相手手番、色をリセット）
 */
export class Game {
  state: GameState;

  constructor(initial: GameState = createInitialState()) {
    this.state = initial;
  }

  // ---------- クエリ ----------

  get phase() {
    return this.state.phase;
  }

  get currentPlayer(): Player {
    return this.state.currentPlayer;
  }

  get winner() {
    return this.state.winner;
  }

  /** 指定プレイヤーが現在選択できる石の種類（交互使用規則・50%連続禁止を含む） */
  getSelectableStones(player: Player = this.state.currentPlayer): StoneType[] {
    return selectableStoneTypes(this.state, player);
  }

  /** 盤面が満杯かどうか */
  isBoardFull(): boolean {
    for (let i = 0; i < BOARD_CELLS; i++) {
      if (this.state.board[i] === null) return false;
    }
    return true;
  }

  /** 現在の手番のプレイヤーが観測できるかどうか */
  canObserve(): boolean {
    return this.state.players[this.state.currentPlayer].observationsUsed < MAX_OBSERVATIONS;
  }

  /** プレイヤーごとの観測使用回数 */
  getObservationCounts(): Record<Player, number> {
    return {
      first: this.state.players.first.observationsUsed,
      second: this.state.players.second.observationsUsed,
    };
  }

  /** 盤面上の石の色配列（index = x + y*10 + z*100、未観測は null） */
  getColors(): (StoneColor | null)[] {
    const colors: (StoneColor | null)[] = new Array(BOARD_CELLS).fill(null);
    for (let i = 0; i < BOARD_CELLS; i++) {
      const stone = this.state.board[i];
      if (stone) colors[i] = stone.observedColor;
    }
    return colors;
  }

  /** 勝利時に成立した5目のライン（黒・白の両方を調べる） */
  getWinLines(): Array<{ color: StoneColor; cells: Array<[number, number, number]> }> {
    if (this.state.phase !== 'over' || this.state.winner === null) return [];
    const colors = this.getColors();
    const lines: Array<{ color: StoneColor; cells: Array<[number, number, number]> }> = [];
    const blackLine = findWinLine(colors, 'black');
    if (blackLine) lines.push({ color: 'black', cells: blackLine });
    const whiteLine = findWinLine(colors, 'white');
    if (whiteLine) lines.push({ color: 'white', cells: whiteLine });
    return lines;
  }

  /**
   * 指定マスに指定種類の石を置けるかどうか（例外を投げない判定）。
   * 理由が欲しい場合は getPlacementError を使う。
   */
  canPlaceStone(x: number, y: number, z: number, type: StoneType): boolean {
    return this.getPlacementError(x, y, z, type) === null;
  }

  /** 配置が不正な場合の日本語エラーメッセージ（合法なら null） */
  getPlacementError(x: number, y: number, z: number, type: StoneType): string | null {
    if (this.state.phase !== 'selecting') {
      return '今は石を置けません。現在の手順を完了してください。';
    }
    if (!inBounds(x, y, z)) {
      return '盤面の範囲外の座標です（0〜9）。';
    }
    if (!this.getSelectableStones().includes(type)) {
      return 'その種類の石は現在使用できません（交互使用規則または50%の連続使用禁止に違反）。';
    }
    if (this.state.board[cellIndex(x, y, z)] !== null) {
      return 'そのマスにはすでに石があります。';
    }
    return null;
  }

  // ---------- アクション ----------

  /**
   * 石を置く。
   * 置いたプレイヤーが観測権を持っていた場合は「観測するか選択中」フェーズへ。
   * 観測権を持たず盤面が満杯なら引き分けで終了。
   * それ以外は相手番へ。
   */
  placeStone(x: number, y: number, z: number, type: StoneType): void {
    const error = this.getPlacementError(x, y, z, type);
    if (error !== null) throw new GameError(error);

    const state = this.state;
    const player = state.currentPlayer;
    const ps = state.players[player];
    const index = cellIndex(x, y, z);

    state.board[index] = { type, owner: player, observedColor: null };
    ps.lastStoneType = type;
    if (type !== SHARED_TYPE) {
      ps.lastRegularType = type;
      ps.hasUsedRegular = true;
    }
    state.moveCount++;

    if (ps.observationsUsed < MAX_OBSERVATIONS) {
      state.phase = 'deciding';
    } else if (this.isBoardFull()) {
      state.winner = 'draw';
      state.phase = 'over';
    } else {
      state.currentPlayer = otherPlayer(player);
      state.phase = 'selecting';
    }
  }

  /**
   * 観測する。盤面上の全石の色を新しい乱数で独立に抽選する。
   * 抽選は決定的な順序（x が最速、次に y、次に z のマス索引順）で実行され、
   * テストで乱数シーケンスを固定して再現できる。
   *
   * 勝敗判定（観測時の色に基づく）:
   * - 黒5目のみ → 先手勝利
   * - 白5目のみ → 後手勝利
   * - 両方 → 観測したプレイヤーの勝利
   * - どちらでもない → 勝者なし（観測権が両者尽きた場合は確認後に引き分け）
   */
  observe(rng: Rng = Math.random): void {
    const state = this.state;
    if (state.phase !== 'deciding') {
      throw new GameError('今は観測できません。');
    }
    const player = state.currentPlayer;
    const ps = state.players[player];
    if (ps.observationsUsed >= MAX_OBSERVATIONS) {
      throw new GameError('観測権を使い切っています。');
    }

    ps.observationsUsed++;

    // 前回の観測結果を引き継がず、全石を新しい乱数で独立に抽選する
    const colors: (StoneColor | null)[] = new Array(BOARD_CELLS).fill(null);
    for (let i = 0; i < BOARD_CELLS; i++) {
      const stone = state.board[i];
      if (stone === null) continue;
      const r = rng();
      const color: StoneColor = r < BLACK_PROBABILITY[stone.type] ? 'black' : 'white';
      stone.observedColor = color;
      colors[i] = color;
    }

    const check: WinCheckResult = checkWin(colors);
    if (check.black || check.white) {
      let winner: Player;
      if (check.black && check.white) {
        // 両色同时に5目 → 観測者が勝利
        winner = player;
      } else {
        winner = check.black ? 'first' : 'second';
      }
      state.winner = winner;
      state.phase = 'over';
    } else if (
      state.players.first.observationsUsed >= MAX_OBSERVATIONS &&
      state.players.second.observationsUsed >= MAX_OBSERVATIONS
    ) {
      // 両者が観測権をすべて使い切り、最後の観測でも勝者なし → 確認後に引き分け
      state.pendingDraw = true;
      state.phase = 'result';
    } else {
      state.pendingDraw = false;
      state.phase = 'result';
    }
  }

  /**
   * 観測せずに次の手番へ進む（観測権は消費されない）。
   * 盤面が満杯の場合は引き分けで終了。
   */
  skipObservation(): void {
    const state = this.state;
    if (state.phase !== 'deciding') {
      throw new GameError('今は観測をスキップできません。');
    }
    if (this.isBoardFull()) {
      state.winner = 'draw';
      state.phase = 'over';
    } else {
      state.currentPlayer = otherPlayer(state.currentPlayer);
      state.phase = 'selecting';
    }
  }

  /**
   * 観測結果（勝者なし）を確認して続行する。
   * すべての石の色をリセットし（位置と種類は維持）、
   * 盤面満杯または双方の観測権枯渇なら引き分け、それ以外は相手番へ。
   */
  confirmResult(): void {
    const state = this.state;
    if (state.phase !== 'result') {
      throw new GameError('今は観測結果を確認できません。');
    }
    const draw = state.pendingDraw || this.isBoardFull();
    for (let i = 0; i < BOARD_CELLS; i++) {
      const stone = state.board[i];
      if (stone) stone.observedColor = null;
    }
    state.pendingDraw = false;
    if (draw) {
      state.winner = 'draw';
      state.phase = 'over';
    } else {
      state.currentPlayer = otherPlayer(state.currentPlayer);
      state.phase = 'selecting';
    }
  }

  /** ゲームを初期状態に戻す */
  restart(): void {
    this.state = createInitialState();
  }

  /** 指定プレイヤーの目標色 */
  targetColor(player: Player = this.state.currentPlayer): StoneColor {
    return TARGET_COLOR[player];
  }
}
